import {
	FormProvider,
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type Recibo, type Constancia } from '@prisma/client'
import { type ActionFunctionArgs } from '@remix-run/node'
import { Form, json, useActionData } from '@remix-run/react'
import { z } from 'zod'
import {
	Field,
	TextareaField,
	ErrorList,
	UsoDeCFDIField,
	FormaDePagoField,
} from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { runWalmart } from '#app/vendors/walmart.ts'

export const WalmartFormSchema = z.object({
	numero_de_ticket: z.string().optional(),
	numero_de_transaccion: z.number().optional(),
	fecha_de_compra: z.date().optional(),
	uso_de_cfdi: z.string().optional(),
	forma_de_pago: z.string().optional(),
	monto_total: z
		.union([z.string().transform(x => x.replace(/[^0-9.-]+/g, '')), z.number()])
		.pipe(z.coerce.number().min(0.0001).max(999999999))
		.optional(),
	observaciones: z.string().optional(),
})

export async function action({ request }: ActionFunctionArgs) {
	const userId = await requireUserId(request)
	const formData = await request.formData()
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
			notes: { select: { id: true, title: true } },
		},
		where: { id: userId },
	})

	const submission = await parseWithZod(formData, {
		schema: WalmartFormSchema.superRefine(async (data, ctx) => {
			const {
				numero_de_ticket,
				numero_de_transaccion,
				uso_de_cfdi,
				forma_de_pago,
			} = data

			if (!numero_de_ticket) {
				ctx.addIssue({
					path: ['numero_de_ticket'],
					code: z.ZodIssueCode.custom,
					message: 'Número de ticket es requerido',
				})
			}
			if (!numero_de_transaccion) {
				ctx.addIssue({
					path: ['numero_de_transaccion'],
					code: z.ZodIssueCode.custom,
					message: 'Número de transacción es requerido',
				})
			}
			if (!uso_de_cfdi) {
				ctx.addIssue({
					path: ['uso_de_cfdi'],
					code: z.ZodIssueCode.custom,
					message: 'Uso de CFDI es requerido',
				})
			}
			if (!forma_de_pago) {
				ctx.addIssue({
					path: ['forma_de_pago'],
					code: z.ZodIssueCode.custom,
					message: 'Forma de Pago es requerido',
				})
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const {
		numero_de_ticket,
		numero_de_transaccion,
		fecha_de_compra,
		uso_de_cfdi,
		forma_de_pago,
		monto_total,
		observaciones,
	} = submission.value

	try {
		let reciboData = {} as (Recibo & { constancia: Constancia }) | null
		reciboData = await prisma.recibo.create({
			data: {
				vendor: 'walmart',
				numero_de_ticket: numero_de_ticket,
				numero_de_transaccion: numero_de_transaccion?.toString(),
				monto_total: monto_total,
				fecha_de_compra: fecha_de_compra,
				observaciones,
				uso_de_cfdi: uso_de_cfdi,
				forma_de_pago: forma_de_pago,
				constancia: {
					connect: {
						userId: owner?.id,
					},
				},
			},
			include: { constancia: true },
		})

		await runWalmart({
			recibo: reciboData,
		})
	} catch (error) {
		json({ result: submission.reply() }, { status: 500 })
	}

	return redirectWithToast(`/recibos`, {
		type: 'success',
		title: 'Success',
		description: 'Has creado tu nuevo recibo.',
	})
}

export default function Test() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()

	const [form, fields] = useForm({
		id: 'walmart-form',
		lastResult: actionData?.result,
		constraint: getZodConstraint(WalmartFormSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: WalmartFormSchema })
		},
	})
	return (
		<FormProvider context={form.context}>
			<Form
				method="POST"
				className="flex h-full w-full flex-col gap-y-4 overflow-y-auto overflow-x-hidden px-10 pb-4 pt-12"
				{...getFormProps(form)}
				encType="multipart/form-data"
			>
				{/*
    This hidden submit button is here to ensure that when the user hits
    "enter" on an input field, the primary form function is submitted
    rather than the first button in the form (which is delete/add image).
*/}
				<button type="submit" className="hidden" />
				<div className="flex flex-col gap-1">
					<Field
						labelProps={{ children: 'Numero de Ticket' }}
						inputProps={{
							...getInputProps(fields.numero_de_ticket, { type: 'text' }),
						}}
						errors={fields.numero_de_ticket.errors}
					/>
					<Field
						labelProps={{ children: 'Número de transacción' }}
						inputProps={{
							...getInputProps(fields.numero_de_transaccion, {
								type: 'number',
							}),
						}}
						errors={fields.numero_de_transaccion.errors}
					/>
					<FormaDePagoField field={fields.forma_de_pago} />
					<UsoDeCFDIField field={fields.uso_de_cfdi} />
					<Field
						labelProps={{ children: 'Monto' }}
						inputProps={{
							...getInputProps(fields.monto_total, {
								type: 'number',
							}),
						}}
						errors={fields.monto_total.errors}
					/>
					<Field
						labelProps={{ children: 'Fecha de transacción' }}
						inputProps={{
							...getInputProps(fields.fecha_de_compra, {
								type: 'date',
							}),
						}}
						errors={fields.fecha_de_compra.errors}
					/>

					<TextareaField
						labelProps={{ children: 'Observaciones' }}
						textareaProps={{
							...getTextareaProps(fields.observaciones),
						}}
						errors={fields.observaciones.errors}
					/>
				</div>

				<ErrorList id={form.errorId} errors={form.errors} />
			</Form>
			<div className="bottom-0 left-3 right-3 flex w-full items-center justify-end gap-2 rounded-lg bg-muted/80 p-4 pl-5 shadow-xl shadow-accent backdrop-blur-sm md:gap-4 md:pl-7">
				<StatusButton
					form={form.id}
					type="submit"
					disabled={isPending}
					status={isPending ? 'pending' : 'idle'}
				>
					Enviar
				</StatusButton>
			</div>
		</FormProvider>
	)
}
